import type { ConvertResult } from '../engine/convert';
import {
  formatBytes,
  formatDuration,
  friendlyResolution,
  type MediaInfo,
} from '../engine/inspect';
import type { ConvertPlan, OutputFormat, PlanOptions } from '../engine/plan';
import { mountDropzone } from './dropzone';

type IdleHandlers = {
  onFile: (file: File) => void;
  onWarmupHint?: () => void;
  /** When true, skip the eyebrow/headline/pitch/chips - the surrounding
   * page already renders them (per-format landing pages). */
  compact?: boolean;
};
/** Landing-page tools. Each opens the options panel on its own section. */
export type ToolPreset = 'compress' | 'trim' | 'rotate' | 'resize' | 'mute' | 'compat';

type InspectHandlers = {
  /** Format initially selected - set by the surrounding landing page. */
  initial: OutputFormat;
  /** Options to start from: the landing page's preset, or the last run's
   *  when the user retries the same file. */
  options: PlanOptions;
  /** Section of the options panel the landing page is about. */
  focus?: ToolPreset | null;
  /** Called when the user switches format or changes an option; returns a
   *  fresh plan to display. */
  replan: (fmt: OutputFormat, options: PlanOptions) => ConvertPlan;
  /** Fires when the user commits to convert. When `streamTarget` is set,
   *  the convert should stream output directly to that file handle (large
   *  outputs - File System Access API path). Absent = classic in-memory
   *  convert with a download button on the done panel. */
  onConvert: (fmt: OutputFormat, options: PlanOptions, streamTarget?: FileSystemWritableFileStream) => void;
  onPickAnother: () => void;
};
type ConvertingHandle = {
  setProgress: (fraction: number) => void;
  setFilename: (name: string) => void;
  /** Stop any timers the handle owns (stall poll, animation frames). Call
   *  when transitioning away from the converting stage so the interval
   *  doesn't leak past unmount. */
  dispose: () => void;
};
type ConvertingHandlers = { onCancel: () => void };
type DoneHandlers = { onConvertAnother: () => void; onSave: () => void };
type ErrorHandlers = { onTryAgain: () => void; onPickAnother: () => void };

// ─── homepage format-picker matrix ────────────────────────────────────
// Turns the passive "READS: MP4 MOV MKV..." pill strip into a live menu of
// every conversion Wave supports, color-coded by mechanism so the fast paths
// pop and the re-encodes are clearly labelled. Data-driven — add a format to
// PICKER_MP4/MP3 below and a cell appears with no other change.
type PickTile = {
  from: string;                                    // display label, e.g. "MKV"
  slug: string;                                    // /{slug}/ target
  mech: 'remux' | 'reencode' | 'cond' | 'extract';
  note: string;                                    // hover caption
};

const PICKER_MP4: PickTile[] = [
  { from: 'MKV',  slug: 'mkv-to-mp4',  mech: 'remux',    note: 'Same streams, new container' },
  { from: 'MOV',  slug: 'mov-to-mp4',  mech: 'remux',    note: 'Extension rename, no re-encode' },
  { from: 'TS',   slug: 'ts-to-mp4',   mech: 'remux',    note: 'MPEG-TS streams copied' },
  { from: 'M4V',  slug: 'm4v-to-mp4',  mech: 'remux',    note: 'Apple’s MP4 variant' },
  { from: '3GP',  slug: '3gp-to-mp4',  mech: 'remux',    note: 'Mobile MP4 profile' },
  { from: 'WEBM', slug: 'webm-to-mp4', mech: 'reencode', note: 'VP9/Opus → H.264/AAC' },
  { from: 'WMV',  slug: 'wmv-to-mp4',  mech: 'reencode', note: 'Microsoft codecs → H.264/AAC' },
  { from: 'MPEG', slug: 'mpeg-to-mp4', mech: 'reencode', note: 'Legacy MPEG → H.264' },
  { from: 'GIF',  slug: 'gif-to-mp4',  mech: 'reencode', note: 'Frames → H.264 video' },
  { from: 'AVI',  slug: 'avi-to-mp4',  mech: 'cond',     note: 'Remux when possible' },
  { from: 'FLV',  slug: 'flv-to-mp4',  mech: 'cond',     note: 'Remux H.264 · re-encode VP6' },
];

const PICKER_MP3: PickTile[] = [
  { from: 'MP4',  slug: 'mp4-to-mp3',  mech: 'extract', note: 'Audio track → 192 kbps MP3' },
  { from: 'MKV',  slug: 'mkv-to-mp3',  mech: 'extract', note: 'Audio track → 192 kbps MP3' },
  { from: 'MOV',  slug: 'mov-to-mp3',  mech: 'extract', note: 'Audio track → 192 kbps MP3' },
  { from: 'WEBM', slug: 'webm-to-mp3', mech: 'extract', note: 'Opus decoded → MP3' },
  { from: 'AVI',  slug: 'avi-to-mp3',  mech: 'extract', note: 'Audio normalized to 192 kbps' },
];

const MECH_LABEL: Record<PickTile['mech'], string> = {
  remux:    'instant',
  reencode: 're-encode',
  cond:     'usually instant',
  extract:  'audio only',
};

function formatPicker(): HTMLElement {
  const root = el('div', { class: 'fp', 'aria-label': 'All supported conversions' });

  const legend = el('div', { class: 'fp-legend' },
    el('span', { class: 'fp-legend-item' },
      el('span', { class: 'fp-dot fp-dot-remux', 'aria-hidden': 'true' }),
      el('span', {}, 'instant remux')),
    el('span', { class: 'fp-legend-item' },
      el('span', { class: 'fp-dot fp-dot-reencode', 'aria-hidden': 'true' }),
      el('span', {}, 're-encode')),
    el('span', { class: 'fp-legend-item' },
      el('span', { class: 'fp-dot fp-dot-cond', 'aria-hidden': 'true' }),
      el('span', {}, 'sometimes remux')),
    el('span', { class: 'fp-legend-item' },
      el('span', { class: 'fp-dot fp-dot-extract', 'aria-hidden': 'true' }),
      el('span', {}, 'extract audio')),
  );

  const group = (heading: string, subheading: string, tiles: PickTile[]): HTMLElement => {
    const wrap = el('div', { class: 'fp-group' });
    const head = el('div', { class: 'fp-group-head' },
      el('span', { class: 'fp-group-heading' }, heading),
      el('span', { class: 'fp-group-sub' }, subheading),
    );
    const grid = el('div', { class: 'fp-grid' });
    for (const t of tiles) {
      const tile = el('a', {
        class: `fp-tile fp-mech-${t.mech}`,
        href: `/${t.slug}/`,
        'aria-label': `Convert ${t.from} — ${t.note}`,
      });
      tile.append(
        el('span', { class: 'fp-from' }, t.from),
        el('span', { class: 'fp-dot', 'aria-hidden': 'true' }),
        el('span', { class: 'fp-mech-label' }, MECH_LABEL[t.mech]),
        el('span', { class: 'fp-note' }, t.note),
      );
      grid.append(tile);
    }
    wrap.append(head, grid);
    return wrap;
  };

  root.append(
    legend,
    group('Into MP4', '11 sources · click any', PICKER_MP4),
    group('Extract audio as MP3', '5 sources', PICKER_MP3),
  );
  return root;
}

// ─── helpers ──────────────────────────────────────────────────────────

const el = <K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | boolean | undefined> = {},
  ...children: (Node | string)[]
): HTMLElementTagNameMap[K] => {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === false) continue;
    if (value === true) node.setAttribute(key, '');
    else node.setAttribute(key, String(value));
  }
  for (const child of children) {
    node.append(child instanceof Node ? child : document.createTextNode(child));
  }
  return node;
};

const arrow = (): HTMLSpanElement => {
  const s = document.createElement('span');
  s.className = 'arrow';
  s.setAttribute('aria-hidden', 'true');
  s.innerHTML =
    '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="M13 6l6 6-6 6"/></svg>';
  return s;
};

const inlineSvg = (svg: string): HTMLSpanElement => {
  const s = document.createElement('span');
  s.setAttribute('aria-hidden', 'true');
  s.style.display = 'inline-flex';
  s.innerHTML = svg;
  return s;
};

const iconCheck = () =>
  inlineSvg('<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="5 12 10 17 20 7"/></svg>');
const iconSparkle = () =>
  inlineSvg('<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v3"/><path d="M12 18v3"/><path d="M3 12h3"/><path d="M18 12h3"/><path d="M5.6 5.6l2.1 2.1"/><path d="M16.3 16.3l2.1 2.1"/><path d="M5.6 18.4l2.1-2.1"/><path d="M16.3 7.7l2.1-2.1"/></svg>');
const iconRepeat = () =>
  inlineSvg('<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>');
const iconDownload = () =>
  inlineSvg('<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12"/><polyline points="7 10 12 15 17 10"/><path d="M4 21h16"/></svg>');
const iconX = () =>
  inlineSvg('<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6l12 12"/><path d="M18 6l-12 12"/></svg>');

const eyebrow = (text: string, opts: { accent?: boolean; withDot?: boolean; inverse?: boolean } = {}): HTMLElement => {
  const div = el('div', { class: `eyebrow${opts.accent ? ' accent' : ''}${opts.inverse ? ' inverse' : ''}` });
  if (opts.withDot) div.append(el('span', { class: 'dot' }));
  div.append(document.createTextNode(text));
  return div;
};

const formatLabel = (fmt: OutputFormat): string => fmt.toUpperCase();

/** Empties the stage root and returns it. */
const swap = (root: HTMLElement): HTMLElement => {
  root.replaceChildren();
  return root;
};

// ─── Idle ─────────────────────────────────────────────────────────────

export function renderIdle(root: HTMLElement, { onFile, onWarmupHint, compact }: IdleHandlers): void {
  swap(root);
  const section = el('section', { id: 'stage-idle', 'aria-labelledby': compact ? undefined : 'idle-headline' });

  let eb: HTMLElement | null = null;
  let headline: HTMLElement | null = null;
  let pitch: HTMLElement | null = null;
  if (!compact) {
    eb = eyebrow('Convert · MP4 · Offline', { accent: true, withDot: true });
    headline = el('h1', { class: 'headline', id: 'idle-headline' });
    (headline as HTMLElement).innerHTML = 'Any video<br />into <span class="accent">MP4.</span>';
    pitch = el(
      'p',
      { class: 'pitch' },
      'The free MP4 converter that runs in your browser. Point it at almost any video - MOV, MKV, AVI, WEBM, WMV, FLV, MTS, MXF, VOB, DivX, F4V, OGV, and more - and Wave rewraps or re-encodes as needed. Optional: pull just the audio out as MP3. Nothing uploaded.'
    );
  }

  const dropzone = el('div', { class: 'dropzone', id: 'dropzone' });
  const heroCard = el('div', { class: 'hero-card' });

  // Landing pages that accept audio-only sources (data-accept-audio="true"
  // on <body>) widen the picker's copy so the CTA doesn't lie. The MP4 side
  // stays "Pick a video to convert" everywhere else.
  const acceptAudio = document.body.dataset.acceptAudio === 'true';
  const pickLabel = acceptAudio ? 'Pick a file to convert' : 'Pick a video to convert';
  const primary = el('button', { type: 'button', class: 'btn btn-primary btn-block', id: 'pick-btn' });
  primary.append(el('span', {}, pickLabel), arrow());
  // Route the button to the (visually hidden) file input.
  primary.addEventListener('click', () => fileInput.click());

  // Small drop icon (inline SVG) beside the drop hint.
  const dropIcon = el('span', { class: 'hero-drop-icon', 'aria-hidden': 'true' });
  dropIcon.innerHTML =
    '<svg viewBox="0 0 20 20" fill="none">' +
      '<path d="M10 3v10" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
      '<path d="M6 9l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>' +
      '<path d="M4 15v2h12v-2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>' +
    '</svg>';
  const meta = el(
    'div',
    { class: 'hero-meta' },
    dropIcon,
    el('span', {}, 'or drop it anywhere on this card')
  );

  // Capability strip — three tiny facts about how Wave runs.
  const caps = el('div', { class: 'hero-caps', 'aria-label': 'Wave runs locally in your browser' });
  const cap = (label: string): HTMLElement =>
    el('span', { class: 'hero-cap' },
       el('span', { class: 'hero-cap-dot', 'aria-hidden': 'true' }),
       el('span', {}, label));
  caps.append(cap('100% local'), cap('WebAssembly + WebCodecs'), cap('up to 8 GB'));

  // Overlay that appears only while a file is dragged over the card.
  const dragOverlay = el('div', { class: 'hero-drop-overlay', 'aria-hidden': 'true' });
  dragOverlay.innerHTML =
    '<svg viewBox="0 0 32 32" fill="none">' +
      '<path d="M16 4v18" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"/>' +
      '<path d="M8 14l8 8 8-8" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>' +
    '<span>Drop to convert</span>';

  heroCard.append(primary, meta, caps, dragOverlay);

  // Format-specific pages advertise video extensions only; the audio-video
  // MP3 hub widens the accept list so the OS picker surfaces audio files too.
  const videoAccept = 'video/*,.mp4,.m4v,.mov,.qt,.mkv,.avi,.webm,.flv,.f4v,.ts,.m2ts,.mts,.3gp,.3g2,.wmv,.asf,.ogv,.ogm,.mpg,.mpeg,.m2v,.m1v,.vob,.mxf,.dv,.y4m,.ivf,.rm,.rmvb';
  const audioAccept = 'audio/*,.mp3,.m4a,.aac,.wav,.flac,.ogg,.opus,.wma,.aiff,.aif,.amr,.ac3,.eac3,.dts,.mka,.caf,.au,.ra,.ape,.tta,.wv';
  const fileInput = el('input', {
    type: 'file',
    class: 'file-input',
    id: 'file-input',
    accept: acceptAudio ? `${videoAccept},${audioAccept}` : videoAccept,
    'aria-label': 'Pick a file to convert',
  }) as HTMLInputElement;

  dropzone.append(heroCard, fileInput);

  if (compact) {
    section.append(dropzone);
  } else {
    section.append(eb!, headline!, pitch!, dropzone, formatPicker());
  }

  root.append(section);

  mountDropzone({ dropzone, fileInput, onFile, onWarmupHint });
}

// ─── Warming (first engine load) ──────────────────────────────────────

export function renderWarming(root: HTMLElement, filename: string): void {
  swap(root);
  const eb = eyebrow('Preparing engine', { accent: true, withDot: true });
  const title = el('h2', { class: 'file-title' }, filename);
  const spinner = el('div', { class: 'spinner', 'aria-hidden': 'true' });
  const note = el('p', { class: 'warming-note' },
    'Wave is loading its conversion engine (~30 MB, once per session). This only happens the first time you convert.'
  );
  const card = el('div', { class: 'card warming-card' }, spinner, note);
  root.append(el('section', { id: 'stage-warming' }, eb, title, card));
}

// ─── Inspect ──────────────────────────────────────────────────────────

export function renderInspect(
  root: HTMLElement,
  info: MediaInfo,
  { initial, options: startOptions, focus, replan, onConvert, onPickAnother }: InspectHandlers
): void {
  swap(root);
  let selected: OutputFormat = initial;
  let options: PlanOptions = { ...startOptions };

  const eb = eyebrow('Selected file', { accent: true, withDot: true });
  const title = el('h2', { class: 'file-title' }, info.filename);

  const rows = el('div', { class: 'summary' });
  const addRow = (label: string, value: string): void => {
    rows.append(
      el('div', { class: 'summary-row' },
        el('span', { class: 'summary-label' }, label),
        el('span', { class: 'summary-value' }, value)
      )
    );
  };
  addRow('Size', formatBytes(info.sizeBytes));
  addRow('Length', formatDuration(info.durationSeconds));
  addRow(
    'Video',
    info.video
      ? `${info.video.codec}${friendlyResolution(info.video.width, info.video.height) ? ` · ${friendlyResolution(info.video.width, info.video.height)}` : ''}`
      : '-'
  );
  addRow('Audio', info.audio ? `${info.audio.codec}${info.audio.channels ? ` · ${info.audio.channels}` : ''}` : '-');
  const summaryCard = el('div', { class: 'card' }, rows);

  // Two-format picker: MP4 (video) or MP3 (audio-only extract from the video).
  // MP3 is disabled when the source has no audio track — attempting the
  // extraction path with no audio makes the wasm ffmpeg encoder abort with
  // an opaque "Aborted()" that surfaces as "The engine couldn't complete
  // this conversion" — worse than telling the user up front.
  const audioMissing = !info.audio;
  // Audio-only sources reach the audio-video-to-mp3 hub with no video track.
  // MP4 is impossible to produce in that case, so disable the button and
  // pin the selection to MP3.
  const videoMissing = !info.video;
  const FORMATS: { key: OutputFormat; label: string; sub: string; disabled?: boolean }[] = [
    { key: 'mp4', label: 'MP4', sub: videoMissing ? 'no video in source' : 'video', disabled: videoMissing },
    { key: 'mp3', label: 'MP3', sub: audioMissing ? 'no audio in source' : 'audio only', disabled: audioMissing },
  ];
  // Reconcile the initial selection with what's actually possible: MP3 gets
  // silently flipped to MP4 on video-only inputs (source dropped on an MP3
  // page but has no audio); MP4 flips to MP3 on audio-only inputs (source
  // has no video, so MP4 is impossible).
  if (audioMissing && selected === 'mp3') selected = 'mp4';
  if (videoMissing && selected === 'mp4') selected = 'mp3';
  const buttons: Record<OutputFormat, HTMLButtonElement> = {} as Record<OutputFormat, HTMLButtonElement>;
  const pickerRow = el('div', { class: 'segmented', role: 'radiogroup', 'aria-label': 'Output format' });
  pickerRow.style.gridTemplateColumns = `repeat(${FORMATS.length}, 1fr)`;
  for (const f of FORMATS) {
    const btnAttrs: Record<string, string> = {
      type: 'button',
      class: 'seg-btn' + (f.disabled ? ' seg-btn-disabled' : ''),
      role: 'radio',
      'aria-checked': f.key === selected ? 'true' : 'false',
      'data-format': f.key,
    };
    if (f.disabled) {
      btnAttrs['aria-disabled'] = 'true';
      btnAttrs['title'] = f.key === 'mp3' ? 'Source has no audio track' : 'Source has no video track';
    }
    const btn = el('button', btnAttrs,
      el('span', { class: 'seg-btn-label' }, f.label),
      el('span', { class: 'seg-btn-sub' }, f.sub)
    ) as HTMLButtonElement;
    if (f.disabled) btn.disabled = true;
    buttons[f.key] = btn;
    pickerRow.append(btn);
  }
  const formatBlock = el('div', { class: 'format-picker' },
    el('div', { class: 'chips-label', style: 'margin-bottom: 10px;' }, 'Output format'),
    pickerRow
  );

  // "Plays everywhere" sits outside the options panel: for an iPhone HEVC or
  // HDR clip it is the one decision most people came to make.
  const compat = compatSwitch(info, options, (on) => {
    options = { ...options, compatibility: on };
    refresh();
  });
  const panel = optionsPanel(info, options, focus ?? null, (next) => {
    options = { ...options, ...next };
    refresh();
  });

  // Plan callout - updates when the user switches format.
  const calloutIconWrap = el('div', { class: 'plan-icon' });
  const calloutTitle = el('div', { class: 'plan-title' });
  const calloutBody = el('p', { class: 'plan-body' });
  const calloutEstimate = el('p', { class: 'plan-estimate' });
  const callout = el('aside', { class: 'plan-callout', 'aria-live': 'polite' },
    calloutIconWrap,
    el('div', {}, calloutTitle, calloutBody, calloutEstimate)
  );

  const primaryLabel = el('span', {}, ctaLabel(selected));
  const primary = el('button', { type: 'button', class: 'btn btn-primary btn-block' });
  primary.append(primaryLabel, arrow());
  // For predicted-large outputs on browsers with File System Access, the
  // primary button opens the OS save dialog first and then streams the
  // convert directly to that file - avoids buffering hundreds of MB in RAM
  // and skips the second click on the done panel. Below the threshold, or
  // on browsers without showSaveFilePicker (Firefox, Safari today), the
  // button behaves the same as it always did.
  let currentPlan: ConvertPlan;
  const FSA_THRESHOLD_BYTES = 500 * 1024 * 1024;
  const hasFsa = typeof (window as { showSaveFilePicker?: unknown }).showSaveFilePicker === 'function';
  primary.addEventListener('click', async () => {
    const plan = currentPlan;
    if (plan.blocker) return;
    const runOptions = optionsFor(selected);
    const shouldStream =
      hasFsa
      && plan.encoder === 'webcodecs'
      && plan.estimatedOutputBytes != null
      && plan.estimatedOutputBytes > FSA_THRESHOLD_BYTES;
    if (!shouldStream) {
      onConvert(selected, runOptions);
      return;
    }
    let stream: FileSystemWritableFileStream | undefined;
    try {
      const picker = (window as unknown as {
        showSaveFilePicker: (opts: {
          suggestedName: string;
          types?: Array<{ description: string; accept: Record<string, string[]> }>;
        }) => Promise<FileSystemFileHandle>;
      }).showSaveFilePicker;
      const suggestedName = `output.${plan.format}`;
      const handle = await picker({
        suggestedName,
        types: [{
          description: plan.format === 'mp3' ? 'MP3 audio' : 'MP4 video',
          accept: { [plan.format === 'mp3' ? 'audio/mpeg' : 'video/mp4']: [`.${plan.format}`] },
        }],
      });
      stream = await handle.createWritable();
    } catch (e) {
      // User dismissed the picker, or the browser refused permission. Fall
      // back to the classic in-memory flow - everything still works, they
      // just get the download button on the done panel instead.
      const name = (e as Error)?.name;
      if (name === 'AbortError' || name === 'NotAllowedError') return;
      onConvert(selected, runOptions);
      return;
    }
    onConvert(selected, runOptions, stream);
  });

  const pickAnother = el('button', { type: 'button', class: 'btn btn-ghost' }, 'Pick another');
  pickAnother.addEventListener('click', onPickAnother);

  const actions = el('div', { class: 'actions row' }, primary, pickAnother);

  const applyPlan = (plan: ConvertPlan): void => {
    currentPlan = plan;
    const blocked = !!plan.blocker;
    calloutIconWrap.replaceChildren(blocked ? iconX() : plan.method === 'remux' ? iconSparkle() : iconRepeat());
    callout.classList.toggle('plan-transcode', plan.method !== 'remux');
    callout.classList.toggle('plan-blocked', blocked);
    calloutTitle.textContent = blocked
      ? 'Can\'t hit that size'
      : plan.method === 'remux'
        ? (plan.format === 'mp3' ? 'Fast extract - no re-encode' : 'Fast copy - no re-encode')
        : (plan.format === 'mp3' ? 'Encoding audio' : 'Re-encode needed');
    calloutBody.textContent = blocked ? plan.blocker! : plan.reason;
    calloutEstimate.textContent = blocked ? '' : formatEstimate(plan);
    calloutEstimate.style.display = calloutEstimate.textContent ? '' : 'none';
    primary.disabled = blocked;
    // If the estimated output is large enough to trigger the FSA streaming
    // path, tell the user what the button will actually do (open OS save
    // dialog first). Otherwise it's the classic "Convert to MP4" flow.
    const wantsStream =
      hasFsa
      && plan.encoder === 'webcodecs'
      && plan.estimatedOutputBytes != null
      && plan.estimatedOutputBytes > FSA_THRESHOLD_BYTES;
    primaryLabel.textContent = wantsStream
      ? (plan.format === 'mp3' ? 'Save MP3 to disk…' : 'Save MP4 to disk…')
      : ctaLabel(plan.format);
  };

  // MP3 output only uses the trim; the video options are hidden and ignored.
  const optionsFor = (fmt: OutputFormat): PlanOptions =>
    fmt === 'mp3' ? { trim: options.trim ?? null } : options;

  const refresh = (): void => {
    compat.root.hidden = selected !== 'mp4' || !compat.relevant;
    panel.setFormat(selected);
    const plan = replan(selected, optionsFor(selected));
    applyPlan(plan);
    panel.setCopyHint(
      plan.format === 'mp4'
      && (plan.method === 'remux' || plan.args.some((a, i) => a === '-c:v' && plan.args[i + 1] === 'copy')),
    );
    panel.setSummary(selected, optionsFor(selected));
  };

  const selectFormat = (fmt: OutputFormat): void => {
    if (fmt === selected) return;
    selected = fmt;
    for (const key of Object.keys(buttons) as OutputFormat[]) {
      buttons[key].setAttribute('aria-checked', key === fmt ? 'true' : 'false');
    }
    refresh();
  };
  for (const f of FORMATS) {
    buttons[f.key].addEventListener('click', () => selectFormat(f.key));
  }
  refresh();

  root.append(
    el('section', { id: 'stage-inspect' },
      eb,
      title,
      summaryCard,
      formatBlock,
      compat.root,
      panel.root,
      callout,
      actions
    )
  );
}

// ─── Options ──────────────────────────────────────────────────────────

/** Decimal megabytes: the unit Discord, WhatsApp and mail providers quote. */
const MB = 1_000_000;

/** Size presets, each labelled with the limit it fits under. WhatsApp: 16 MB
 *  per video in chats. Email: Gmail and Outlook.com cap a message at 25 MB
 *  after attachments are base64-encoded, which adds about a third, so about
 *  18 MB of file. Discord: 20 MB on the free tier since 13 August 2026. */
const SIZE_PRESETS: { mb: number; label: string }[] = [
  { mb: 16, label: 'WhatsApp' },
  { mb: 18, label: 'Email' },
  { mb: 20, label: 'Discord' },
];

const RESOLUTIONS: { edge: number; label: string }[] = [
  { edge: 1080, label: '1080p' },
  { edge: 720, label: '720p' },
  { edge: 480, label: '480p' },
];

const ROTATIONS: { deg: 0 | 90 | 180 | 270; label: string }[] = [
  { deg: 0, label: 'None' },
  { deg: 90, label: '90° right' },
  { deg: 180, label: '180°' },
  { deg: 270, label: '90° left' },
];

/** True when the source copies into MP4 as something some players can't
 *  show: HEVC (no decoder on many Windows PCs), AV1, 10-bit or HDR. */
function needsCompat(info: MediaInfo): boolean {
  const v = info.video;
  if (!v) return false;
  const c = v.codec.toLowerCase();
  const isH264 = c === 'h264' || c === 'avc1' || c === 'avc';
  const copyable = isH264 || c === 'hevc' || c === 'h265' || c === 'av1';
  return copyable && (!isH264 || v.highBitDepth || !!v.hdrTransfer);
}

function compatSwitch(
  info: MediaInfo,
  options: PlanOptions,
  onChange: (on: boolean) => void,
): { root: HTMLElement; relevant: boolean } {
  const relevant = needsCompat(info);
  const v = info.video;
  const what = v?.hdrTransfer
    ? 'This video is HDR. Wave can re-encode it to standard H.264 and convert the colours so it doesn\'t look washed out on ordinary screens.'
    : v && /hevc|h265/i.test(v.codec)
      ? 'This video is HEVC (H.265), which many Windows PCs and older TVs can\'t play without an extra codec. Wave can re-encode it to H.264, which plays everywhere.'
      : 'Wave can re-encode this video to 8-bit H.264, the format every phone, PC, TV and browser plays.';
  const input = el('input', { type: 'checkbox', class: 'switch-input', id: 'opt-compat', role: 'switch' }) as HTMLInputElement;
  input.checked = !!options.compatibility;
  input.addEventListener('change', () => onChange(input.checked));
  const root = el('label', { class: 'compat-card', for: 'opt-compat' },
    el('span', { class: 'compat-text' },
      el('span', { class: 'compat-title' }, 'Make it play everywhere'),
      el('span', { class: 'compat-body' }, what + ' Slower than a straight copy.'),
    ),
    el('span', { class: 'switch' }, input, el('span', { class: 'switch-track', 'aria-hidden': 'true' })),
  );
  root.hidden = !relevant;
  return { root, relevant };
}

type OptionsPanelHandle = {
  root: HTMLElement;
  setFormat: (fmt: OutputFormat) => void;
  /** The current MP4 plan copies the video, so a trim is keyframe-aligned
   *  unless "Exact cut" is ticked. */
  setCopyHint: (copies: boolean) => void;
  setSummary: (fmt: OutputFormat, options: PlanOptions) => void;
};

/** Collapsible panel with size, resolution, trim, rotate and audio options.
 *  Every change calls `onChange` with the fields it touched; the inspect
 *  stage re-plans so the callout and estimate update live. */
function optionsPanel(
  info: MediaInfo,
  start: PlanOptions,
  focus: ToolPreset | null,
  onChange: (next: Partial<PlanOptions>) => void,
): OptionsPanelHandle {
  const v = info.video;
  const srcShort = v ? Math.min(v.displayWidth ?? v.width ?? 0, v.height ?? 0) : 0;
  const duration = info.durationSeconds ?? 0;

  const details = el('details', { class: 'opts' }) as HTMLDetailsElement;
  const summaryValue = el('span', { class: 'opts-summary-value' });
  const summary = el('summary', { class: 'opts-summary' },
    el('span', { class: 'opts-summary-label' }, 'Options'),
    summaryValue,
    inlineSvg('<svg class="opts-chevron" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>'),
  );
  const body = el('div', { class: 'opts-body' });
  details.append(summary, body);

  const section = (key: ToolPreset, label: string, ...children: HTMLElement[]): HTMLElement => {
    const s = el('div', { class: 'opts-row', 'data-opt': key },
      el('div', { class: 'opts-label' }, label),
      ...children,
    );
    body.append(s);
    return s;
  };

  /** A row of pill buttons with radio semantics. */
  const pills = <T,>(
    name: string,
    items: { value: T; label: string; sub?: string }[],
    current: T,
    pick: (value: T) => void,
  ): { el: HTMLElement; set: (value: T) => void } => {
    const group = el('div', { class: 'opt-pills', role: 'radiogroup', 'aria-label': name });
    const btns: { value: T; btn: HTMLButtonElement }[] = [];
    for (const item of items) {
      const btn = el('button', { type: 'button', class: 'opt-pill', role: 'radio', 'aria-checked': 'false' },
        el('span', { class: 'opt-pill-label' }, item.label),
        ...(item.sub ? [el('span', { class: 'opt-pill-sub' }, item.sub)] : []),
      ) as HTMLButtonElement;
      btn.addEventListener('click', () => { set(item.value); pick(item.value); });
      btns.push({ value: item.value, btn });
      group.append(btn);
    }
    const set = (value: T): void => {
      for (const b of btns) b.btn.setAttribute('aria-checked', b.value === value ? 'true' : 'false');
    };
    set(current);
    return { el: group, set };
  };

  // ── File size ──
  const sizeRows: HTMLElement[] = [];
  if (v) {
    const presets = SIZE_PRESETS.filter((p) => p.mb * MB < info.sizeBytes);
    const startMb = start.targetBytes ? Math.round((start.targetBytes / MB) * 10) / 10 : null;
    const isPreset = startMb != null && presets.some((p) => p.mb === startMb);
    const customInput = el('input', {
      type: 'number', class: 'opt-input opt-input-mb', min: '1', step: '1', inputmode: 'decimal',
      'aria-label': 'Target size in megabytes', placeholder: 'MB',
    }) as HTMLInputElement;
    if (startMb != null && !isPreset) customInput.value = String(startMb);
    const customWrap = el('span', { class: 'opt-custom' }, customInput, el('span', { class: 'opt-unit' }, 'MB'));
    type SizeChoice = number | 'original' | 'custom';
    const items: { value: SizeChoice; label: string; sub?: string }[] = [
      { value: 'original', label: 'Original' },
      ...presets.map((p) => ({ value: p.mb as SizeChoice, label: `${p.mb} MB`, sub: p.label })),
      { value: 'custom', label: 'Custom' },
    ];
    const startChoice: SizeChoice = startMb == null ? 'original' : isPreset ? startMb : 'custom';
    customWrap.hidden = startChoice !== 'custom';
    const readCustom = (): number | null => {
      const n = Number(customInput.value);
      return Number.isFinite(n) && n > 0 ? Math.round(n * MB) : null;
    };
    const size = pills<SizeChoice>('File size', items, startChoice, (value) => {
      customWrap.hidden = value !== 'custom';
      if (value === 'custom') {
        customInput.focus();
        onChange({ targetBytes: readCustom() });
      } else {
        onChange({ targetBytes: value === 'original' ? null : value * MB });
      }
    });
    customInput.addEventListener('input', () => onChange({ targetBytes: readCustom() }));
    sizeRows.push(section('compress', 'File size',
      size.el, customWrap,
      el('p', { class: 'opts-hint' }, 'Wave picks the bitrate, and the resolution if it has to, to land just under the size.'),
    ));
  }

  // ── Resolution ──
  const resolutions = RESOLUTIONS.filter((r) => srcShort > r.edge);
  if (v && resolutions.length) {
    const startEdge = start.maxShortEdge ?? null;
    const res = pills<number | null>('Resolution',
      [{ value: null, label: 'Original', sub: friendlyResolution(v.width, v.height) ?? undefined },
        ...resolutions.map((r) => ({ value: r.edge as number | null, label: r.label }))],
      startEdge,
      (value) => onChange({ maxShortEdge: value }),
    );
    sizeRows.push(section('resize', 'Resolution', res.el));
  }

  // ── Trim ──
  const startInput = el('input', {
    type: 'text', class: 'opt-input opt-input-time', inputmode: 'decimal', spellcheck: 'false',
    'aria-label': 'Start time', placeholder: '0:00',
  }) as HTMLInputElement;
  const endInput = el('input', {
    type: 'text', class: 'opt-input opt-input-time', inputmode: 'decimal', spellcheck: 'false',
    'aria-label': 'End time', placeholder: duration ? clock(duration) : 'end',
  }) as HTMLInputElement;
  if (start.trim) {
    startInput.value = clock(start.trim.start);
    endInput.value = clock(start.trim.end);
  }
  const exact = el('input', { type: 'checkbox', id: 'opt-exact' }) as HTMLInputElement;
  exact.checked = !!start.trim?.exact;
  const exactRow = el('label', { class: 'opt-check', for: 'opt-exact' }, exact,
    el('span', {}, 'Exact cut', el('span', { class: 'opt-check-sub' }, ' - re-encodes so the clip starts on the exact frame instead of the nearest keyframe')));
  const trimHint = el('p', { class: 'opts-hint' });
  const readTrim = (): PlanOptions['trim'] => {
    const s = startInput.value.trim() ? parseClock(startInput.value) : 0;
    const e = endInput.value.trim() ? parseClock(endInput.value) : duration || null;
    startInput.setAttribute('aria-invalid', s == null ? 'true' : 'false');
    endInput.setAttribute('aria-invalid', e == null ? 'true' : 'false');
    if (s == null || e == null) return null;
    if (e <= s) {
      endInput.setAttribute('aria-invalid', 'true');
      return null;
    }
    if (!startInput.value.trim() && !endInput.value.trim()) return null;
    return { start: s, end: e, exact: exact.checked };
  };
  const onTrim = (): void => onChange({ trim: readTrim() });
  startInput.addEventListener('input', onTrim);
  endInput.addEventListener('input', onTrim);
  exact.addEventListener('change', onTrim);
  const trimRow = section('trim', 'Trim',
    el('div', { class: 'opt-trim' },
      el('label', { class: 'opt-field' }, el('span', { class: 'opt-field-label' }, 'Start'), startInput),
      el('span', { class: 'opt-trim-dash', 'aria-hidden': 'true' }, '–'),
      el('label', { class: 'opt-field' }, el('span', { class: 'opt-field-label' }, 'End'), endInput),
    ),
    exactRow,
    trimHint,
  );
  trimHint.textContent = 'Times as minutes:seconds, like 1:05 or 0:12.5.';

  // ── Rotate ──
  const videoRows: HTMLElement[] = [];
  if (v) {
    const rot = pills<0 | 90 | 180 | 270>('Rotate', ROTATIONS.map((r) => ({ value: r.deg, label: r.label })),
      start.rotate ?? 0, (value) => onChange({ rotate: value }));
    videoRows.push(section('rotate', 'Rotate', rot.el));
  }

  // ── Audio ──
  if (v && info.audio) {
    const mute = el('input', { type: 'checkbox', id: 'opt-mute' }) as HTMLInputElement;
    mute.checked = !!start.removeAudio;
    mute.addEventListener('change', () => onChange({ removeAudio: mute.checked }));
    videoRows.push(section('mute', 'Audio',
      el('label', { class: 'opt-check', for: 'opt-mute' }, mute, el('span', {}, 'Remove the audio track'))));
  }

  if (focus && focus !== 'compat') {
    details.open = true;
    body.querySelector<HTMLElement>(`[data-opt="${focus}"]`)?.classList.add('is-focus');
  } else if (start.trim || start.rotate || start.removeAudio || start.targetBytes || start.maxShortEdge) {
    details.open = true;
  }

  return {
    root: details,
    setFormat(fmt) {
      // MP3 output keeps only the trim.
      for (const r of [...sizeRows, ...videoRows]) r.hidden = fmt === 'mp3';
      trimRow.hidden = false;
    },
    setCopyHint(copies) {
      const hasTrim = !!(startInput.value.trim() || endInput.value.trim());
      exactRow.hidden = !hasTrim || (!copies && !exact.checked);
    },
    setSummary(fmt, o) {
      const bits: string[] = [];
      if (fmt === 'mp4') {
        if (o.targetBytes) bits.push(`under ${Math.round((o.targetBytes / MB) * 10) / 10} MB`);
        if (o.maxShortEdge) bits.push(`${o.maxShortEdge}p`);
      }
      if (o.trim) bits.push(`${clock(o.trim.start)}–${clock(o.trim.end)}`);
      if (fmt === 'mp4') {
        if (o.rotate) bits.push(`rotated ${o.rotate === 270 ? '90° left' : o.rotate === 90 ? '90° right' : '180°'}`);
        if (o.removeAudio) bits.push('no audio');
      }
      summaryValue.textContent = bits.length ? bits.join(' · ') : 'Trim, rotate, resize, compress';
      summaryValue.classList.toggle('is-set', bits.length > 0);
    },
  };
}

/** 83.5 → "1:23.5"; whole seconds drop the decimal. */
function clock(seconds: number): string {
  const s = Math.max(0, seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s - h * 3600 - m * 60;
  const secText = Number.isInteger(Math.round(sec * 10) / 10) ? String(Math.round(sec)) : (Math.round(sec * 10) / 10).toFixed(1);
  const padded = secText.padStart(secText.includes('.') ? 4 : 2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${padded}` : `${m}:${padded}`;
}

/** Accepts "75", "1:15", "1:15.5", "0:01:15". Returns seconds or null. */
function parseClock(text: string): number | null {
  const parts = text.trim().replace(',', '.').split(':');
  if (parts.length > 3 || parts.some((p) => !/^\d+(\.\d+)?$/.test(p))) return null;
  return parts.reduce((acc, p) => acc * 60 + Number(p), 0);
}

const ctaLabel = (fmt: OutputFormat): string =>
  fmt === 'mp3' ? 'Extract as MP3' : 'Convert to MP4';

function formatEstimate(plan: ConvertPlan): string {
  const parts: string[] = [];
  if (plan.estimatedOutputBytes != null) {
    parts.push(`~${formatBytes(plan.estimatedOutputBytes)}`);
  }
  if (plan.estimatedDurationMs != null) {
    parts.push(`~${formatDuration(plan.estimatedDurationMs / 1000)}`);
  }
  if (!parts.length) return '';
  const prefix = plan.method === 'remux' ? 'Result' : 'Estimated';
  return `${prefix}: ${parts.join(' · ')}`;
}

// ─── Converting ───────────────────────────────────────────────────────

export function renderConverting(
  root: HTMLElement,
  initialFilename: string,
  format: OutputFormat,
  { onCancel }: ConvertingHandlers
): ConvertingHandle {
  swap(root);

  const eb = eyebrow(`Converting to ${formatLabel(format)}`, { withDot: true, inverse: true });

  const pctDigits = el('span', { class: 'pct-digits' }, '0');
  const pctSymbol = el('span', { class: 'pct-symbol' }, '%');
  const pct = el('div', { class: 'pct' }, pctDigits, pctSymbol);

  const track = el('div', { class: 'track' }, el('div', { class: 'track-fill' }));
  const fill = track.querySelector('.track-fill') as HTMLElement;

  const filename = el('div', { class: 'converting-filename' }, initialFilename);

  const eta = el('div', { class: 'converting-eta' }, '');

  const note = el(
    'p',
    { class: 'converting-note' },
    'Everything happens in your browser. Keep this tab open until it finishes.'
  );

  const cancelBtn = el('button', { type: 'button', class: 'btn btn-cancel' },
    iconX(),
    el('span', {}, 'Cancel')
  );
  cancelBtn.addEventListener('click', onCancel);

  const converting = el(
    'section',
    { id: 'stage-converting', class: 'converting' },
    eb,
    filename,
    pct,
    track,
    eta,
    note,
    el('div', { class: 'actions', style: 'margin-top: 8px;' }, cancelBtn)
  );

  root.append(converting);

  // Smoothly tween the numeric display so it doesn't feel jumpy - the actual
  // ffmpeg progress events arrive in coarse bursts. Values are clamped so
  // display never runs past the last real number by more than a step.
  let displayed = 0;
  let target = 0;
  let rafId = 0;
  const tick = (): void => {
    const diff = target - displayed;
    if (Math.abs(diff) < 0.0005) {
      displayed = target;
    } else {
      displayed += diff * 0.15;
    }
    const pct = Math.max(0, Math.min(100, displayed * 100));
    pctDigits.textContent = String(Math.floor(pct));
    fill.style.width = `${pct}%`;
    if (displayed !== target) rafId = requestAnimationFrame(tick);
    else rafId = 0;
  };

  // Live ETA. Extrapolate `remaining = elapsed × (1 - f) / f`, smoothed with
  // an exponential moving average so the number doesn't jitter when the
  // encoder's throughput bursts. Deliberately silent for the first 5% of
  // progress and the first two seconds - early samples are dominated by
  // startup costs (wasm load, first-keyframe latency) and would print a
  // wildly pessimistic estimate.
  let startedAt = 0;
  let smoothedRemainingMs: number | null = null;
  let etaLastUpdate = 0;
  let lastProgressAt = 0;
  const ETA_MIN_FRACTION = 0.05;
  const ETA_MIN_ELAPSED_MS = 2000;
  const ETA_EMA_ALPHA = 0.25;
  const ETA_REFRESH_MS = 500;
  // If no progress event fires for this long, switch to indeterminate copy
  // ("Processing - X elapsed"). Path B on unknown-duration sources goes
  // this route; also protects against long ffmpeg segments that stall
  // between `time=` log lines.
  const STALL_MS = 8000;
  const updateEta = (fraction: number): void => {
    if (!startedAt) startedAt = performance.now();
    lastProgressAt = performance.now();
    if (fraction >= 1) { eta.textContent = 'Almost done'; return; }
    const elapsedMs = performance.now() - startedAt;
    if (fraction < ETA_MIN_FRACTION || elapsedMs < ETA_MIN_ELAPSED_MS) return;
    const remaining = elapsedMs * (1 - fraction) / fraction;
    smoothedRemainingMs = smoothedRemainingMs == null
      ? remaining
      : smoothedRemainingMs * (1 - ETA_EMA_ALPHA) + remaining * ETA_EMA_ALPHA;
    if (performance.now() - etaLastUpdate < ETA_REFRESH_MS) return;
    etaLastUpdate = performance.now();
    eta.textContent = `About ${formatDuration(smoothedRemainingMs / 1000)} remaining`;
  };

  // Poll for stall: if we haven't heard a progress update in STALL_MS,
  // switch the ETA line to an elapsed-time counter. Real progress events
  // reset `lastProgressAt` so this only surfaces during genuine silence.
  const stallInterval = window.setInterval(() => {
    if (!startedAt) return;
    if (performance.now() - lastProgressAt < STALL_MS) return;
    const elapsed = (performance.now() - startedAt) / 1000;
    eta.textContent = `Processing - ${formatDuration(elapsed)} elapsed`;
  }, 1000);

  return {
    setProgress(fraction) {
      target = Math.max(0, Math.min(1, fraction));
      if (!rafId) rafId = requestAnimationFrame(tick);
      updateEta(target);
    },
    setFilename(name) {
      filename.textContent = name;
    },
    dispose() {
      window.clearInterval(stallInterval);
      if (rafId) cancelAnimationFrame(rafId);
    },
  };
}

// ─── Done ─────────────────────────────────────────────────────────────

export function renderDone(
  root: HTMLElement,
  result: ConvertResult,
  { onConvertAnother, onSave }: DoneHandlers
): void {
  swap(root);

  const badge = el('div', { class: 'done-badge' });
  badge.append(iconCheck());
  const badgeRow = el('div', { class: 'done-badge-row' }, badge, eyebrow('Done', { accent: true }));

  const label = formatLabel(result.format);
  const headline = el('h2', { class: 'headline done-headline' });
  headline.innerHTML = result.format === 'mp3'
    ? `Audio's an <span class="accent">${label} now.</span>`
    : `It's an <span class="accent">${label} now.</span>`;

  const rows = el('div', { class: 'summary' });
  const addRow = (label: string, value: string): void => {
    rows.append(
      el('div', { class: 'summary-row' },
        el('span', { class: 'summary-label' }, label),
        el('span', { class: 'summary-value' }, value)
      )
    );
  };
  // Streamed path: mp4-mux wrote directly to the user's chosen file, so the
  // returned blob is empty. Detect that here so we skip the "Save" button
  // (the file's already on disk) and label the summary accordingly.
  const streamedToDisk = result.blob.size === 0;
  addRow('Saved as', result.outputName);
  // A size target is in decimal megabytes (the unit Discord and WhatsApp
  // quote), so report the result in the same unit next to it.
  const target = result.plan.targetBytes;
  if (!streamedToDisk) {
    addRow('Size', target
      ? `${(result.blob.size / MB).toFixed(1)} MB (target ${Math.round((target / MB) * 10) / 10} MB)`
      : formatBytes(result.blob.size));
  }
  addRow('Method', result.method === 'remux' ? 'Fast copy' : 'Re-encoded');
  addRow('Engine', engineLabel(result));
  addRow('Took', formatDuration(result.durationMs / 1000));

  const card = el('div', { class: 'card' }, rows);

  // Surface any post-encode warnings (e.g. A/V drift) as a small block
  // under the summary. Kept subdued - the output is still usable; the note
  // just tells the user why something might look/sound slightly off.
  let warnBlock: HTMLElement | null = null;
  if (result.warnings && result.warnings.length) {
    warnBlock = el('div', { class: 'done-warnings' });
    for (const w of result.warnings) {
      warnBlock.append(el('div', { class: 'done-warning' }, w));
    }
  }

  const anotherBtn = el('button', { type: 'button', class: 'btn btn-secondary btn-block' });
  anotherBtn.append(el('span', {}, 'Convert another'), arrow());
  anotherBtn.addEventListener('click', onConvertAnother);

  // On the streamed path there's nothing to save - the file's already on
  // disk at the location the user picked. Skip the Save button and promote
  // "Convert another" to primary so the page has one clear next action.
  let actions: HTMLDivElement;
  if (streamedToDisk) {
    anotherBtn.classList.remove('btn-secondary');
    anotherBtn.classList.add('btn-primary');
    actions = el('div', { class: 'actions row' }, anotherBtn);
  } else {
    const saveBtn = el('button', { type: 'button', class: 'btn btn-primary btn-block' });
    saveBtn.append(el('span', {}, `Save ${label}`), iconDownload());
    saveBtn.addEventListener('click', onSave);
    actions = el('div', { class: 'actions row' }, saveBtn, anotherBtn);
  }

  root.append(
    el('section', { id: 'stage-done' },
      badgeRow, headline, card,
      ...(warnBlock ? [warnBlock] : []),
      actions,
    )
  );
}

/** Human-readable label for which backend actually ran. Shown in the done
 *  panel so the "why is this so fast?" crowd sees the answer. */
function engineLabel(result: ConvertResult): string {
  if (result.method === 'remux') return 'Container rewrap (no re-encode)';
  const plan = result.plan;
  if (plan.encoder === 'webcodecs') {
    const codec = plan.videoCodec === 'hevc' ? 'HEVC' : 'H.264';
    const source = plan.decodeStrategy === 'ffmpeg'
      ? 'ffmpeg decode + WebCodecs encode'
      : plan.decodeStrategy === 'webcodecs'
        ? 'browser decode + WebCodecs encode'
        : 'WebCodecs (hardware)';
    return `${codec} via ${source}`;
  }
  if (result.format === 'mp3') return 'MP3 via LAME (on-device ffmpeg)';
  const args = plan.args;
  const videoCopied = args.some((a, i) => a === '-c:v' && args[i + 1] === 'copy');
  if (videoCopied) return 'Video copied, audio re-encoded to AAC';
  return 'ffmpeg (mpeg4 fallback)';
}

// ─── Error ────────────────────────────────────────────────────────────

export function renderError(
  root: HTMLElement,
  message: string,
  { onTryAgain, onPickAnother }: ErrorHandlers
): void {
  swap(root);

  const badge = el('div', { class: 'err-badge' });
  badge.append(iconX());
  const badgeRow = el('div', { class: 'done-badge-row' }, badge, eyebrow('Something went wrong', { accent: false }));

  const headline = el('h2', { class: 'headline done-headline' });
  headline.innerHTML = 'That <span class="accent">didn\'t work.</span>';

  const card = el('div', { class: 'card err-card' },
    el('p', { class: 'err-message' }, message)
  );

  const primary = el('button', { type: 'button', class: 'btn btn-primary btn-block' });
  primary.append(el('span', {}, 'Try again'), arrow());
  primary.addEventListener('click', onTryAgain);

  const secondary = el('button', { type: 'button', class: 'btn btn-secondary btn-block' });
  secondary.append(el('span', {}, 'Pick another file'), arrow());
  secondary.addEventListener('click', onPickAnother);

  const actions = el('div', { class: 'actions row' }, primary, secondary);

  root.append(
    el('section', { id: 'stage-error' }, badgeRow, headline, card, actions)
  );
}
